import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import { Composer, Container, Header, MessageList, useWebchat } from "@botpress/webchat";
import { Edit } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import Cookies from "js-cookie";
import { useSnackbar } from "notistack";

import constants from "../../assets/constants.js";
import TextInputDialog from "../dialogs/textInputDialog.js";
import Checkbox from "../ui/omniCheckbox";
import OmniMenuItem from "../ui/omniMenuItem.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import OmniTextField from "../ui/omniTextField.js";
import Widget from "../ui/widget.js";
import WidgetContent from "../ui/widgetContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const BotpressTestHarness = props => {
  const { botpressBotODM, userODM, widgetSize = "large", minHeight = "333px" } = props;
  const theme = useTheme();

  const { enqueueSnackbar } = useSnackbar();
  const webchatClientID = botpressBotODM?.omniData.webchatClientId;

  const calculateUserData = (theData, userODM) => {
    try {
      //try is excessive but saftey first
      if (userODM && theData) {
        theData.firstName = userODM.omniData.givenName;
        theData.lastName = userODM.omniData.familyName;
      }
    } catch (error) {}
    return theData;
  };

  const [userData, setUserData] = useState(
    calculateUserData(botpressBotODM?.omniData.userData, userODM),
  );

  const [editUserData, setEditUserData] = useState(null);

  const [botData, setBotData] = useState(botpressBotODM?.omniData.botData);
  const [editBotData, setEditBotData] = useState(null);

  const [languageCode, setLanguageCode] = useState(botpressBotODM?.omniData.userData.languageCode);

  const botDisplayName = botpressBotODM?.omniData.botData?.botName;

  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const [newConversationOnLaunch, setNewConversationOnLaunch] = useState(true);

  const { client, messages, isTyping, user, clientState, newConversation } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here
    feedbackEnabled: true,
  });

  const enrichedMessages = useMemo(
    () =>
      messages.map(message => {
        const { authorId } = message;
        const direction = authorId === user?.userId ? "outgoing" : "incoming";
        return {
          ...message,
          direction,
          sender:
            direction === "outgoing"
              ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
              : { name: botData?.botName ?? "Bot", avatar: botData?.botAvatar },
        };
      }),
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [botData?.botName, messages, user?.userId, user?.name, user?.pictureUrl],
  );

  const endConversation = () => {
    try {
      const omniPayload = {
        omniEvent: "endConversation",
        omniData: {},
      };
      client.sendEvent(omniPayload).then(() => {
        setIsWebchatOpen(false);
        enqueueSnackbar("Any Feedback you left will be available in a few minutes", {
          variant: "success",
        });
      });
    } catch (error) {
      console.error("Error ending conversation:", error);
    }
  };

  const startWebchat = () => {
    if (newConversationOnLaunch) {
      newConversation().then(() => {
        setIsWebchatOpen(true);
      });
    } else {
      setIsWebchatOpen(true);
    }
  };

  const [userInitialized, SetUserInitialized] = useState(false);

  useEffect(() => {
    if (client && userData && !userInitialized) {
      try {
        client.updateUser({
          data: JSON.parse(userData),
        });
        SetUserInitialized(true);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, userData, userInitialized]);

  useEffect(() => {
    const handleBeforeUnload = event => {
      console.log("User is attempting to close the tab");
      client?.disconnect();
      const allCookies = Cookies.get(); // Gets an object with all cookie names and values
      for (const cookieName in allCookies) {
        if (Object.hasOwnProperty.call(allCookies, cookieName)) {
          Cookies.remove(cookieName);
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      {!isWebchatOpen && (
        <Widget widgetSize={"full"} minHeight={"100%"}>
          <WidgetTitle title={"Launch Pad"} text={botDisplayName}>
            <OmniResponsiveButton
              tooltipTitle="Edit the User Settings"
              buttonText="User"
              sx={{ marginRight: theme.spacing(2) }}
              onClick={() => {
                setEditUserData(JSON.stringify(userData, null, 2));
              }}
              icon={<Edit />}
            />
            <OmniResponsiveButton
              tooltipTitle="Edit the AI Settings"
              buttonText="Bot"
              onClick={() => {
                setEditBotData(JSON.stringify(botData, null, 2));
              }}
              icon={<Edit />}
            />
          </WidgetTitle>
          <WidgetContent>
            <Grid container size={{ xs: 12 }}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    spacing: "8px",
                    marginRight: theme.spacing(2),
                    marginLeft: theme.spacing(2),
                    gap: theme.spacing(2),
                    padding: theme.spacing(1),
                  }}
                >
                  <OmniTextField
                    label="Initial Language"
                    select
                    variant="outlined"
                    id={"language"}
                    value={languageCode}
                    onChange={event => {
                      setLanguageCode(event.target.value);
                      let newUserData = JSON.parse(userData);
                      newUserData.languageCode = event.target.value;
                      setUserData(JSON.stringify(newUserData, null, 2));
                    }}
                    placeholder={"language"}
                    required
                  >
                    <OmniMenuItem key={"en-US"} value={"en-US"}>
                      {"English (US)"}
                    </OmniMenuItem>
                    <OmniMenuItem key={"es-US"} value={"es-US"}>
                      {"Spanish (US)"}
                    </OmniMenuItem>
                    <OmniMenuItem key={"ru-RU"} value={"ru-RU"}>
                      {"Russian (RU)"}
                    </OmniMenuItem>
                    <OmniMenuItem key={"sw-KE"} value={"sw-KE"}>
                      {"Swahili (KE)"}
                    </OmniMenuItem>
                  </OmniTextField>
                </Box>
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    spacing: "8px",
                    marginRight: theme.spacing(2),
                    marginLeft: theme.spacing(2),
                    marginTop: theme.spacing(1),

                    gap: theme.spacing(2),
                    padding: theme.spacing(1),
                  }}
                >
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={newConversationOnLaunch}
                          onChange={() => setNewConversationOnLaunch(!newConversationOnLaunch)}
                          name="newConversationOnLaunch"
                        />
                      }
                      label="Always start with a new conversation."
                    />
                  </FormGroup>
                </Box>
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    spacing: "8px",
                    margin: theme.spacing(2),
                    gap: theme.spacing(2),
                    padding: theme.spacing(1),

                    alignSelf: "flex-end",
                    justifyContent: "flex-end",
                    textAlign: "right",
                  }}
                >
                  <Button
                    sx={{ width: "100%", height: "100" }}
                    tooltipTitle="Start Conversation"
                    variant="contained"
                    onClick={() => {
                      startWebchat();
                    }}
                  >
                    <Stack>
                      <Typography
                        variant="h4"
                        sx={{
                          borderBottom: `2px solid `,
                          margin: theme.spacing(2),
                          paddingBottom: theme.spacing(2),
                          marginBottom: 0,
                        }}
                      >
                        Start Conversation
                      </Typography>

                      <Typography
                        variant="h5"
                        sx={{
                          margin: theme.spacing(2),
                        }}
                      >
                        Click Here
                      </Typography>
                    </Stack>
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </WidgetContent>
        </Widget>
      )}
      {isWebchatOpen && (
        <Container
          connected={clientState !== "disconnected"}
          style={{
            height: "755px",
            display: isWebchatOpen ? "flex" : "none",
          }}
        >
          <Header
            // onOpenChange={() => console.log('Override the header open change')}
            defaultOpen={false}
            closeWindow={() => endConversation()}
            // closeWindow={() => setIsWebchatOpen(false)}
            // restartConversation={newConversation}
            disabled={false}
            configuration={botData}
          />
          <MessageList
            botAvatar={botData?.botAvatar}
            botName={botData?.botName}
            botDescription={botData?.botDescription}
            isTyping={isTyping}
            headerMessage="Chat History"
            showMarquee={true}
            messages={enrichedMessages}
            sendMessage={client?.sendMessage}
            addMessageFeedback={client?.addMessageFeedback}
          />
          <Composer
            disableComposer={false}
            isReadOnly={false}
            allowFileUpload={true}
            connected={clientState !== "disconnected"}
            sendMessage={client?.sendMessage}
            uploadFile={client?.uploadFile}
            composerPlaceholder="Type a message..."
          />
        </Container>
      )}

      {editUserData && (
        <TextInputDialog
          inputLabel={`User Data JSON`}
          open={editUserData}
          dialogTitle={"Update User Data"}
          dialogMessage={`Please enter some well formed JSON.`}
          defaultValue={editUserData}
          inputPlaceholder={"User Data JSON"}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          maxWidth={"sm"}
          rows={10}
          onAccept={theValue => {
            try {
              //validate
              setUserData(theValue);
              setEditUserData(null);
            } catch (error) {
              setEditUserData(null);
              console.log(error);
            }
          }}
          onClose={() => {
            setEditUserData(null);
          }}
        />
      )}

      {editBotData && (
        <TextInputDialog
          inputLabel={`Bot Configuration JSON`}
          open={editBotData}
          dialogTitle={"Update Bot Configuration"}
          dialogMessage={`Please enter some well formed JSON.`}
          defaultValue={editBotData}
          inputPlaceholder={"Bot Configuration JSON"}
          omniInputType={constants.omniImputType.text}
          multiline={true}
          maxWidth={"sm"}
          rows={15}
          onAccept={theValue => {
            try {
              //validate
              setBotData(theValue);
              setEditBotData(null);
            } catch (error) {
              setEditBotData(null);
              console.log(error);
            }
          }}
          onClose={() => {
            setEditBotData(null);
          }}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  botpressBotODM: state.botpressSession.botpressBotODM,
  userODM: state.userSession.userODM,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BotpressTestHarness));
